import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Clock, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { articlesData } from '../data/articles';
import ReactMarkdown from 'react-markdown';

export const ArticleDetailPage = () => {
  const { slug } = useParams();
  const article = articlesData[slug];

  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-[#1A1A1C] tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/articles">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              All Articles
            </Button>
          </Link>
        </div>
      </header>

      {/* Article Header */}
      <section className="pt-32 pb-12 px-6 bg-gradient-to-b from-[#F8F8F8] to-white">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex items-center gap-4 text-[#5A6270] text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {article.publishDate}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {article.readTime}
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {article.author}
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A1C] mb-6 leading-tight">
            {article.title}
          </h1>
          
          <p className="text-xl text-[#5A6270] leading-relaxed">
            {article.description}
          </p>
          
          <div className="mt-8 h-1 w-32 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] rounded-full" />
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg prose-headings:text-[#1A1A1C] prose-headings:font-bold prose-p:text-[#1A1A1C] prose-p:leading-relaxed prose-a:text-[#C9A64A] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#1A1A1C] prose-strong:font-bold prose-ul:text-[#1A1A1C] prose-ol:text-[#1A1A1C] prose-blockquote:border-l-4 prose-blockquote:border-[#C9A64A] prose-blockquote:pl-6 prose-blockquote:italic max-w-none article-content">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to implement this in your business?
          </h2>
          <p className="text-xl text-gray-300 font-light mb-10 leading-relaxed">
            Reading guides is valuable. Having an expert Virtual CFO implement them is transformative.
          </p>
          <Link to="/">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 px-10 py-6 text-lg font-bold rounded-xl"
            >
              Work with Virtual CFO
            </Button>
          </Link>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-20 px-6 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-[#1A1A1C] mb-8">Continue Reading</h3>
          <div className="flex gap-4">
            <Link to="/articles">
              <Button variant="outline" className="text-[#C9A64A] border-[#C9A64A] hover:bg-[#C9A64A]/10">
                View All Articles
              </Button>
            </Link>
            <Link to="/calculators">
              <Button variant="outline" className="text-[#C9A64A] border-[#C9A64A] hover:bg-[#C9A64A]/10">
                Try Free Calculators
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
